import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@zola/zola-ui/src/components/Link';
import { formatArrayWithOxfordComma } from '@zola-helpers/client/dist/es/transformers/formatArrayWithOxfordComma';

import Accordion from './Accordion';
import gift from '../../assets/images/icons/gift.svg';
import {
  formatExpShippingDate,
  getShippingText,
  deliverySurchargeCopy,
  getPersonalizedArrivalCopy,
  returnHolidayShippingMessage,
} from './helpers';
import featureFlags from '../../util/featureFlags';
import { formatPrice } from '../../util/currencyFormatter';
import './TabPanel.less';

const ReadMoreDescription = (props) => {
  const { description, editorial, type } = props;
  const [showReadMore, setShowReadMore] = useState(true);
  const editorialDescription = editorial ? (
    <div className="editorial-description">
      <span>Why We Love It:</span> {editorial}
    </div>
  ) : null;
  const readMore = () => {
    const contentId = `product-description-${type}`;
    const content = document.getElementById(contentId);
    if (showReadMore) {
      content.classList.remove('shorten');
      setShowReadMore(false);
    } else if (!showReadMore) {
      content.classList.add('shorten');
      setShowReadMore(true);
    }
  };
  if (description.length > 500) {
    return (
      <div id={`product-description-${type}`} className="product-description_container shorten">
        {editorialDescription}
        {/* eslint-disable-next-line */}
        <div className="product-description" dangerouslySetInnerHTML={{ __html: description }} />
        <div className="gradient" />
        <div className="read-more" onClick={readMore}>
          {showReadMore ? 'Read More' : 'Close'}
        </div>
      </div>
    );
  }
  return (
    <>
      {editorialDescription}
      {/* eslint-disable-next-line */}
      <div
        itemProp="description"
        className="product-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </>
  );
};

// TODO: move this back into svc-web-api
const getAttributesText = (attributes) => {
  let str;
  attributes.forEach((attribute) => {
    if (!str) {
      str = attribute.label.toLowerCase();
    } else {
      str = `${str}/${attribute.label.toLowerCase()}`;
    }
  });
  return str;
};

const ContentWithTabs = (props) => {
  const { productData, activeProductLook, type, shipDates, productAttributes, activeSku } = props;
  if (!productData || !activeProductLook) return null;
  const [currentTab, setCurrentTab] = useState(0);
  const freeShippingMax = shipDates && shipDates.freeShippingMax;
  const freeShippingMin = shipDates && shipDates.freeShippingMin;
  const expShippingMax = shipDates && shipDates.expShippingMax;
  const expShippingMin = shipDates && shipDates.expShippingMin;
  const numAttributes = productData.attributes.length;
  const shippingZones = productData.shipping_zones;
  const shippingExcludedStates = productData.shipping_excluded_states;
  const deliverySurcharge = activeSku && activeSku.delivery_surcharge_cents;

  useEffect(() => {
    setCurrentTab(0);
  }, [activeProductLook]);

  const selectAttributeText = `To see this gift’s estimated arrival, please select a ${getAttributesText(
    productData.attributes
  )}.`;
  const personalizedReturnPolicy =
    productData.personalizable && 'However, if personalized, it cannot be exchanged or returned. ';
  const personalizedArrivalCopy = productData.personalizable && getPersonalizedArrivalCopy();
  const highlightTab = (e) => {
    const index = e.target.getAttribute('index');
    setCurrentTab(Number(index));
  };

  const renderHolidayShipping = () => {
    const holidayShippingMessage = returnHolidayShippingMessage(
      productData,
      freeShippingMin,
      expShippingMax,
      activeSku
    );

    return holidayShippingMessage ? (
      <div className="shipping-text">
        <span className="holiday-arrival-question">
          <img src={gift} alt="" className="holiday-arrival-question__icon" /> Want it by 12/24?
        </span>
        <span className="shipping-text holiday-arrival-suggestion">{` ${holidayShippingMessage}`}</span>
      </div>
    ) : null;
  };

  const brandUrl = `/shop/brand/${productData.brand.key}`;
  const contentTabsClass = `content-tabs_container ${type}`;

  return (
    <div className={contentTabsClass}>
      <div className="product-tabs">
        <button
          // eslint-disable-next-line react/no-unknown-property
          index="0"
          onClick={highlightTab}
          className={`description ${currentTab === 0 ? 'current' : ''}`}
        >
          Description
          {currentTab === 0 ? <span className="tab-highlight" /> : null}
        </button>
        <button
          // eslint-disable-next-line react/no-unknown-property
          index="1"
          onClick={highlightTab}
          className={`details ${currentTab === 1 ? 'current' : ''}`}
        >
          Details
          {currentTab === 1 ? <span className="tab-highlight" /> : null}
        </button>
        <button
          // eslint-disable-next-line react/no-unknown-property
          index="2"
          onClick={highlightTab}
          className={`arrival ${currentTab === 2 ? 'current' : ''}`}
        >
          {type === 'desktop' ? 'Estimated Arrival' : 'Est. Arrival'}
          {currentTab === 2 ? <span className="tab-highlight" /> : null}
        </button>
        <button
          // eslint-disable-next-line react/no-unknown-property
          index="3"
          onClick={highlightTab}
          className={`shipping ${currentTab === 3 ? 'current' : ''}`}
        >
          Shipping & Returns
          {currentTab === 3 ? <span className="tab-highlight" /> : null}
        </button>
      </div>
      <div className="content_container">
        {currentTab === 0 ? (
          <ReadMoreDescription
            type={type}
            editorial={productData.editorial_description}
            description={productData.description}
          />
        ) : null}
        {currentTab === 1 ? (
          <div className="product-details">
            {productData.detail_items.map((detail) => (
              <div key={detail.label}>
                {detail.label}: {detail.value}
              </div>
            ))}
            <div>
              Brand:{' '}
              <Link target="_blank" rel="noopener noreferrer" href={brandUrl}>
                {productData.brand.name}
              </Link>
            </div>
          </div>
        ) : null}
        {currentTab === 2 ? (
          <div className="product-arrival">
            {Object.keys(productAttributes).length >= numAttributes ? (
              <>
                {personalizedArrivalCopy && (
                  <div className="shipping-text">{personalizedArrivalCopy}</div>
                )}
                <div className="shipping-text">
                  {getShippingText(productData.ship_method, deliverySurcharge)}: {freeShippingMin} -{' '}
                  {freeShippingMax}
                </div>
                {expShippingMax && expShippingMin ? (
                  <div className="shipping-text">
                    {formatExpShippingDate(expShippingMin, expShippingMax)}
                  </div>
                ) : null}
                {renderHolidayShipping()}
                {productData.custom_shipping_message ? (
                  <div className="shipping-text">{productData.custom_shipping_message}</div>
                ) : null}
              </>
            ) : (
              selectAttributeText
            )}
          </div>
        ) : null}
        {currentTab === 3 ? (
          <div className="product-shipping">
            {!!deliverySurcharge && (
              <div className="shipping-method-text">
                {deliverySurchargeCopy} {formatPrice(deliverySurcharge)}
              </div>
            )}
            <div className="shipping-method-text">Shipping Method: {productData.ship_method}</div>
            <div className="return-policy-text">
              Return Policy: {productData.return_policy} {personalizedReturnPolicy}
              <Link arrow target="_blank" rel="noopener noreferrer" href="/returns">
                See details
              </Link>
            </div>
            {!featureFlags.get('shippingZonesV2') &&
            productData.ship_method !== 'E-delivery' &&
            shippingZones.indexOf('US_ALASKA_AND_HAWAII') === -1 ? (
              <div className="shipping-exclusion-text">
                <span>Please note this gift cannot be shipped to Alaska or Hawaii. </span>
                <Link
                  arrow
                  href="/faq/360000894232-do-you-ship-to-alaska-and-hawaii-"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Find out why
                </Link>
              </div>
            ) : null}
            {featureFlags.get('shippingZonesV2') &&
            productData.ship_method !== 'E-delivery' &&
            shippingExcludedStates.length > 0 ? (
              <div className="shipping-exclusion-text">
                <span>
                  Please note this gift cannot be shipped to{' '}
                  {formatArrayWithOxfordComma(shippingExcludedStates, 'or')}.
                </span>{' '}
                <Link
                  arrow
                  href="/faq/where-can-zola-products-be-shipped"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Find out why
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const AccordionPanel = (props) => {
  const { productData, activeProductLook, shipDates, activeSku } = props;
  if (!productData || !activeProductLook || !shipDates) return null;
  const deliverySurcharge = activeSku && activeSku.delivery_surcharge_cents;
  const items = [
    { id: 0, heading: 'Description', description: productData.description, type: 'description' },
    {
      id: 1,
      heading: 'Details',
      details: productData.detail_items,
      brand: productData.brand,
      type: 'details',
    },
    {
      id: 2,
      heading: 'Estimated Arrival',
      shipDates,
      type: 'arrival',
      productData,
      shippingText: getShippingText(productData.ship_method, deliverySurcharge),
      customShippingMessage: productData.custom_shipping_message,
      deliverySurcharge,
    },
    {
      id: 3,
      heading: 'Shipping & Returns',
      shipMethod: productData.ship_method,
      returnPolicy: productData.return_policy,
      shippingZones: productData.shipping_zones,
      shippingExcludedStates: productData.shipping_excluded_states,
      type: 'shipping',
      deliverySurcharge,
    },
  ];
  if (!shipDates.freeShippingMin) {
    items.splice(2, 1);
  }
  return <Accordion allowMultiple items={items} activeSku={activeSku} />;
};

ContentWithTabs.propTypes = {
  productData: PropTypes.shape({}), // fetchProductByKey payload
  activeProductLook: PropTypes.shape({}), // fetchProductByKey payload > product_look_views
  activeSku: PropTypes.shape({}), // fetchProductByKey payload > sku_previews
  shipDates: PropTypes.shape({}),
  productAttributes: PropTypes.shape({}),
  type: PropTypes.string,
};

AccordionPanel.propTypes = {
  productData: PropTypes.shape({}), // fetchProductByKey payload
  activeProductLook: PropTypes.shape({}), // fetchProductByKey payload > product_look_views
  activeSku: PropTypes.shape({}), // fetchProductByKey payload > sku_previews
  shipDates: PropTypes.shape({}),
};

export default ContentWithTabs;
